import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { contactFormSchema, type ContactFormData } from '@/lib/contactSchema';
import { Checkbox } from '@/components/ui/checkbox';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [honeypot, setHoneypot] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof ContactFormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetId = useRef<string | null>(null);

  const TURNSTILE_SITE_KEY = '0x4AAAAAACafR7IVSLGt_n6O';

  const renderTurnstile = useCallback(() => {
    if (turnstileRef.current && (window as any).turnstile && !turnstileWidgetId.current) {
      turnstileWidgetId.current = (window as any).turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token: string) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(null),
        'error-callback': () => setTurnstileToken(null),
        theme: 'light',
      });
    }
  }, []);

  useEffect(() => {
    if (document.querySelector('script[src*="turnstile"]')) {
      renderTurnstile();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.onload = () => renderTurnstile();
    document.head.appendChild(script);
  }, [renderTurnstile]);

  const contactInfo = [
    {
      icon: Phone,
      title: "Telefon",
      value: "+386 70 846 087",
      description: "Dosegljivi smo vsak delovnik"
    },
    {
      icon: Mail,
      title: "E-pošta",
      value: "info@atren.eu",
      description: "Odgovorimo v 24 urah"
    },
    {
      icon: MapPin,
      title: "Naslov",
      value: "Partizanska cesta 71d, 6210 Sežana",
      description: "Obiščite nas v naših prostorih"
    },
    {
      icon: Clock,
      title: "Delovni čas",
      value: "Pon-Pet 8:00-17:00",
      description: "Za nujne primere tudi zunaj delovnika"
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name as keyof ContactFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    // Validate form data
    const validation = contactFormSchema.safeParse(formData);
    if (!validation.success) {
      const fieldErrors: Partial<Record<keyof ContactFormData, string>> = {};
      validation.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as keyof ContactFormData] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          ...validation.data,
          website: honeypot,
          turnstileToken,
        },
      });

      if (error) {
        throw new Error('Napaka pri pošiljanju');
      }

      if (data?.success) {
        toast({
          title: "Sporočilo poslano!",
          description: "Hvala za vaše sporočilo. Kontaktirali vas bomo v najkrajšem možnem času.",
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
        setHoneypot('');
        setConsentGiven(false);
        setTurnstileToken(null);
        if (turnstileWidgetId.current && (window as any).turnstile) {
          (window as any).turnstile.reset(turnstileWidgetId.current);
        }
      } else {
        throw new Error(data?.error || 'Napaka pri pošiljanju');
      }
    } catch (error) {
      toast({
        title: "Napaka",
        description: error instanceof Error ? error.message : "Prišlo je do napake. Poskusite znova.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="kontakt" className="py-20 bg-gradient-subtle">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 animate-fade-in-up">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Stopite v <span className="text-atren-orange">stik</span> z nami
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Imate vprašanja o naših storitvah ali se zanima za sodelovanje? 
            Radi se pogovorimo o vaših potrebah in najdemo najboljšo rešitev.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-6 animate-slide-in-right">
            {contactInfo.map((info, index) => (
              <Card key={index} className="border-0 bg-white/80 backdrop-blur-sm hover:shadow-primary transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-atren-orange/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-6 h-6 text-atren-orange" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">
                        {info.title}
                      </h3>
                      <p className="text-foreground font-medium mb-1">
                        {info.value}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {info.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Quick Contact */}
            <Card className="border-0 bg-gradient-primary text-white">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-3">
                  Potrebujete takojšen odgovor?
                </h3>
                <p className="text-white/90 mb-4 text-sm">
                  Pokličite nas neposredno za hitro svetovanje in informacije o naših storitvah.
                </p>
                <Button
                  variant="secondary"
                  className="w-full bg-white text-atren-orange hover:bg-atren-gray-light"
                  onClick={() => window.open('tel:+38670846087')}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Pokličite zdaj
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2 animate-fade-in-up">
            <Card className="border-0 bg-white/90 backdrop-blur-sm shadow-elegant">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-foreground mb-6">
                  Pošljite nam sporočilo
                </h3>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot field - hidden from users, catches bots */}
                  <div className="absolute opacity-0 pointer-events-none h-0 overflow-hidden" aria-hidden="true">
                    <label htmlFor="website">Website</label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-foreground font-medium">
                        Ime in priimek *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className={`mt-2 border-atren-gray-light focus:border-atren-orange focus:ring-atren-orange ${errors.name ? 'border-destructive' : ''}`}
                        placeholder="Vaše ime"
                      />
                      {errors.name && <p className="text-sm text-destructive mt-1">{errors.name}</p>}
                    </div>
                    
                    <div>
                      <Label htmlFor="email" className="text-foreground font-medium">
                        E-pošta *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className={`mt-2 border-atren-gray-light focus:border-atren-orange focus:ring-atren-orange ${errors.email ? 'border-destructive' : ''}`}
                        placeholder="vas.email@example.com"
                      />
                      {errors.email && <p className="text-sm text-destructive mt-1">{errors.email}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone" className="text-foreground font-medium">
                        Telefon
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`mt-2 border-atren-gray-light focus:border-atren-orange focus:ring-atren-orange ${errors.phone ? 'border-destructive' : ''}`}
                        placeholder="+386 xx xxx xxx"
                      />
                      {errors.phone && <p className="text-sm text-destructive mt-1">{errors.phone}</p>}
                    </div>
                    
                    <div>
                      <Label htmlFor="subject" className="text-foreground font-medium">
                        Zadeva *
                      </Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        className={`mt-2 border-atren-gray-light focus:border-atren-orange focus:ring-atren-orange ${errors.subject ? 'border-destructive' : ''}`}
                        placeholder="Povpraševanje o storitvah"
                      />
                      {errors.subject && <p className="text-sm text-destructive mt-1">{errors.subject}</p>}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-foreground font-medium">
                      Sporočilo *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className={`mt-2 border-atren-gray-light focus:border-atren-orange focus:ring-atren-orange resize-none ${errors.message ? 'border-destructive' : ''}`}
                      placeholder="Opišite vaše potrebe ali vprašanja..."
                    />
                    {errors.message && <p className="text-sm text-destructive mt-1">{errors.message}</p>}
                  </div>

                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="consent"
                      checked={consentGiven}
                      onCheckedChange={(checked) => setConsentGiven(checked === true)}
                      className="mt-1"
                    />
                    <Label htmlFor="consent" className="text-sm text-muted-foreground font-normal leading-relaxed cursor-pointer">
                      Strinjam se z obdelavo osebnih podatkov v skladu s{' '}
                      <a href="/politika-zasebnosti" target="_blank" className="text-atren-orange-dark hover:underline">
                        politiko zasebnosti
                      </a>. *
                    </Label>
                  </div>

                  <div ref={turnstileRef} className="flex justify-center" />

                  <Button
                    type="submit"
                    disabled={isSubmitting || !consentGiven || !turnstileToken}
                    className="w-full bg-gradient-primary shadow-primary hover:shadow-elegant transition-all duration-300 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      "Pošiljamo..."
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Pošljite sporočilo
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;